import { useEffect, useRef, useState } from "react";
import {
  Checkbox,
  FormControlLabel,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Layers } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import "./MapboxMapComponent.css";
import { getIcon } from "../../../modules/getIcon.js";
import defaultIconLayer from "../../../assets/images/Synchro/events-icons/defaultIconLayer.png";
import { useMaterialUIController } from "context";

function buildLayerVisibilityState(layers, previousVisibility = {}) {
  const nextVisibility = layers.reduce((acc, layer) => {
    acc[layer.id] = previousVisibility[layer.id] ?? (layer.visible ?? true);
    return acc;
  }, {});

  layers
    .filter((layer) => layer.isGroup && Array.isArray(layer.children))
    .forEach((layer) => {
      const childIds = layer.children.filter((childId) => childId in nextVisibility);
      nextVisibility[layer.id] = childIds.length
        ? childIds.every((childId) => !!nextVisibility[childId])
        : nextVisibility[layer.id];
    });

  return nextVisibility;
}

function loadIconsToMap(map, icons = []) {
  return Promise.all(
    icons.map(({ id, path }) => {
      return new Promise((resolve) => {
        if (map.hasImage(id)) return resolve();
        map.loadImage(path, (err, image) => {
          if (!err && image && !map.hasImage(id)) {
            map.addImage(id, image);
          }
          resolve();
        });
      });
    })
  );
}

function buildPopupActionsHtml({ basePath, itemId, viewTitle, editTitle }) {
  if (!itemId) {
    return "";
  }

  return `
    <div style="display:flex;gap:8px;margin-top:10px;justify-content:flex-end;">
      <a
        href="${basePath}/${itemId}"
        title="${viewTitle}"
        style="display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:6px;background:#1f2a44;color:#ffffff;text-decoration:none;"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      </a>
      <a
        href="${basePath}/${itemId}/edit"
        title="${editTitle}"
        style="display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:6px;background:#1976d2;color:#ffffff;text-decoration:none;"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 20h9"></path>
          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
        </svg>
      </a>
    </div>
  `;
}

function buildAccidentPopupHtml(message, accidentId) {
  if (!message?.trim()) {
    return "";
  }

  if (!accidentId) {
    return `<strong>${message}</strong>`;
  }

  const actionsHtml = buildPopupActionsHtml({
    basePath: "/list/accidents",
    itemId: accidentId,
    viewTitle: "Προβολή",
    editTitle: "Επεξεργασία",
  });

  return `<div><strong>${message}</strong>${actionsHtml}</div>`;
}

function buildInterventionPopupHtml(message, interventionId) {
  if (!message?.trim()) {
    return "";
  }

  if (!interventionId) {
    return `<strong>${message}</strong>`;
  }

  const actionsHtml = buildPopupActionsHtml({
    basePath: "/list/interventions",
    itemId: interventionId,
    viewTitle: "Προβολή",
    editTitle: "Επεξεργασία",
  });

  return `<div><strong>${message}</strong>${actionsHtml}</div>`;
}

function MapboxMapComponent({ layers, mapUIConfig }) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const hoveredFeatureId = useRef(null);
  const clickedFeatureId = useRef({});
  const popupRef = useRef(
    new mapboxgl.Popup({ closeButton: false, closeOnClick: false })
  );

  mapboxgl.accessToken = mapUIConfig.map.mapToken;

  const initialMapStyle =
    mapUIConfig.defaultMapStyle === "3D"
      ? mapUIConfig.map.style.map3D
      : mapUIConfig.map.style.map2D;

  const [selectedMapStyle, setSelectedMapStyle] = useState(initialMapStyle);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [layerVisibility, setLayerVisibility] = useState(() =>
    buildLayerVisibilityState(layers)
  );

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [openLayersModal, setOpenLayersModal] = useState(false);
  const panelBackground = darkMode ? "#202940" : "#ffffff";
  const panelText = darkMode ? "#fff" : "#344767";
  const panelBorder = darkMode
    ? "1px solid rgba(255,255,255,0.12)"
    : "1px solid rgba(0,0,0,0.12)";
  const panelShadow = darkMode
    ? "0 8px 24px rgba(0,0,0,0.3)"
    : "0 8px 24px rgba(0,0,0,0.12)";
  const groupedLayers = layers.filter((layer) => layer.isGroup);
  const topLevelLayers = layers.filter((layer) => !layer.parentId);
  const layerRows = topLevelLayers.flatMap((layer) => {
    const childLayers = groupedLayers.some((groupLayer) => groupLayer.id === layer.id)
      ? layers.filter((childLayer) => childLayer.parentId === layer.id)
      : [];

    return [{ layer, isChild: false }, ...childLayers.map((childLayer) => ({ layer: childLayer, isChild: true }))];
  });

  useEffect(() => {
    setLayerVisibility((prev) => buildLayerVisibilityState(layers, prev));
  }, [layers]);

  const prepareAndRenderIconLayer = (map, layers, renderLayers) => {
    const iconLayers = layers.filter((l) => !l.isGroup && l.layer?.includes("IconLayer"));

    iconLayers.forEach((layer) => {
      const features = layer?.source?.data?.features || [];
      const { useSourceIcon = false, setIcon } = layer.layerStyle || {};
      const usedIcons = new Set();

      features.forEach((f) => {
        if (!f.id) f.id = crypto.randomUUID();

        if (useSourceIcon) {
          // Use icon from feature if exists
          if (f.properties?.icon) {
            usedIcons.add(f.properties.icon);
          } else {
            // Fallback icon per layer
            const fallbackId = `fallback-${layer.id}`;
            f.properties.icon = fallbackId;
            usedIcons.add(fallbackId);

            const iconPath = setIcon || defaultIconLayer;
            if (!map.hasImage(fallbackId)) {
              map.loadImage(iconPath, (err, image) => {
                if (!err && image && !map.hasImage(fallbackId)) {
                  map.addImage(fallbackId, image);
                }
              });
            }
          }
        } else {
          // Icon comes from setIcon or default
          const fallbackId = setIcon
            ? `icon-set-${layer.id}`
            : `fallback-${layer.id}`;
          f.properties.icon = fallbackId;
          usedIcons.add(fallbackId);

          const iconPath = setIcon || defaultIconLayer;
          if (!map.hasImage(fallbackId)) {
            map.loadImage(iconPath, (err, image) => {
              if (!err && image && !map.hasImage(fallbackId)) {
                map.addImage(fallbackId, image);
              }
            });
          }
        }
      });

      if (layer?.source?.data) {
        layer.source.data.features = features;
      }

      const iconsToLoad = Array.from(usedIcons)
        .filter(
          (id) =>
            !map.hasImage(id) &&
            !id.startsWith("icon-set-") &&
            !id.startsWith("fallback-")
        )
        .map((id) => ({ id, path: getIcon(id) }));

      if (iconsToLoad.length) {
        loadIconsToMap(map, iconsToLoad);
      }
    });

    const waitForStyle = () => {
      if (map.isStyleLoaded()) {
        renderLayers(map);
      } else {
        requestAnimationFrame(waitForStyle);
      }
    };

    waitForStyle();
  };

  // Init Map
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: selectedMapStyle,
      center: mapUIConfig.map.center,
      zoom: mapUIConfig.map.zoom,
      pitch: mapUIConfig.map.pitch,
      bearing: mapUIConfig.map.bearing,
      minZoom: mapUIConfig.map.minZoom,
      maxZoom: mapUIConfig.map.maxZoom,
    });

    mapRef.current = map;

    map.once("style.load", () => {
      prepareAndRenderIconLayer(map, layers, renderLayers);
    });

    return () => map.remove();
  }, []); // keep empty like before

  // Sync 2D base style when dark mode changes
  useEffect(() => {
    if (selectedMapStyle !== mapUIConfig.map.style.map3D) {
      setSelectedMapStyle(mapUIConfig.map.style.map2D);
    }
  }, [mapUIConfig.map.style.map2D]);

  // Change Map Style
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    map.setStyle(selectedMapStyle);
    map.once("style.load", () => {
      prepareAndRenderIconLayer(map, layers, renderLayers);
    });
  }, [selectedMapStyle]);

  // Change Map Layer source
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    prepareAndRenderIconLayer(map, layers, renderLayers);
  }, [layers]);

  // Fly to when center/zoom change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    map.flyTo({
      center: mapUIConfig.map.center,
      zoom: mapUIConfig.map.zoom,
      essential: true,
    });
  }, [mapUIConfig.map.center, mapUIConfig.map.zoom]);

  const createLineLayer = (layerId, sourceId, options = {}) => ({
    id: layerId,
    type: "line",
    source: sourceId,
    layout: {
      "line-cap": "round",
      "line-join": "round",
      ...(options.layout || {}),
    },
    paint: {
      "line-emissive-strength": options.lineEmissiveStrength || 1.0,
      "line-color": [
        "case",
        ["has", "colorLine"],
        ["to-color", ["get", "colorLine"]],
        options.color || "#3399ff",
      ],
      "line-width": [
        "case",
        ["boolean", ["feature-state", "click"], false],
        options.clickedWidth || 8,
        options.lineWidth || 3,
      ],
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "click"], false],
        options.clickedOpacity || 0.4,
        ["boolean", ["feature-state", "hover"], false],
        options.hoverOpacity || 0.5,
        options.defaultOpacity || 1,
      ],
      ...(options.paint || {}),
    },
  });

const createPointLayer = (layerId, sourceId, options = {}) => ({
    id: layerId,
    type: "circle",
    source: sourceId,
    layout: {
      visibility: options.visibility || "visible",
      ...(options.layout || {}),
    },
    paint: {
      "circle-emissive-strength": options.lineEmissiveStrength || 1.0,
      "circle-radius": options.fixedSize
        ? options.radius || 6
        : [
            "interpolate",
            ["linear"],
            ["zoom"],
            5,
            options.radius || 6,
            15,
            options.radius || 6,
          ],
      "circle-color": [
        "case",
        ["has", "color"],
        ["to-color", ["get", "color"]],
        ["to-color", options.color || "#00aaff"],
      ],
      "circle-opacity": [
        "case",
        ["boolean", ["feature-state", "click"], false],
        options.clickedOpacity || 0.6,
        ["boolean", ["feature-state", "hover"], false],
        options.hoverOpacity || 0.7,
        options.defaultOpacity || 1,
      ],
      "circle-stroke-color": options.strokeColor || "#ffffff",
      "circle-stroke-width": options.strokeWidth || 1,
      ...(options.paint || {}),
    },
  });

  const createFillLayer = (layerId, sourceId, options = {}) => ({
    id: layerId,
    type: "fill",
    source: sourceId,
    layout: {
      ...(options.layout || {}),
    },
    paint: {
      "fill-emissive-strength": options.lineEmissiveStrength || 1.0,
      "fill-color": [
        "case",
        ["has", "colorFill"],
        ["to-color", ["get", "colorFill"]],
        options.color || "#ffcc00",
      ],
      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "click"], false],
        options.clickedOpacity || 0.4,
        ["boolean", ["feature-state", "hover"], false],
        options.hoverOpacity || 0.6,
        options.defaultOpacity || 0.8,
      ],
      ...(options.paint || {}),
    },
  });

  const createIconLayer = (layerId, sourceId, options = {}) => {
    const defaultIcon = options.defaultIcon;

    return {
      id: layerId,
      type: "symbol",
      source: sourceId,
      layout: {
        "icon-image": defaultIcon
          ? ["coalesce", ["get", "icon"], defaultIcon]
          : ["get", "icon"],
        "icon-size": options.iconSize || 0.9,
        "icon-allow-overlap": true,
        ...(options.layout || {}),
      },
      paint: {
        "icon-opacity": [
          "case",
          ["boolean", ["feature-state", "click"], false],
          0.6,
          ["boolean", ["feature-state", "hover"], false],
          0.8,
          1,
        ],
        ...(options.paint || {}),
      },
    };
  };

  const renderLayers = (map) => {
    hoveredFeatureId.current = null;
    clickedFeatureId.current = {};
    popupRef.current.remove();

    layers.forEach((layer) => {
      if (layer.isGroup) {
        return;
      }

      const sourceId = `${layer.id}-source`;
      const visibility = layerVisibility[layer.id] ? "visible" : "none";

      if (map.getLayer(layer.id)) map.removeLayer(layer.id);
      if (map.getSource(sourceId)) map.removeSource(sourceId);

      if (layer.source?.data) {
        map.addSource(sourceId, {
          ...layer.source,
          ...(layer.cluster
            ? { cluster: true, clusterMaxZoom: 14, clusterRadius: 50 }
            : {}),
        });
      }

      if (layer.layer.includes("LineLayer")) {
        const lineLayer = createLineLayer(layer.id, sourceId, {
          ...layer.layerStyle,
          layout: {
            ...(layer.layerStyle?.layout || {}),
            visibility,
          },
        });
        map.addLayer(lineLayer);
      }

      if (layer.layer.includes("PointLayer")) {
        const pointLayer = createPointLayer(layer.id, sourceId, {
          ...layer.layerStyle,
          visibility,
          layout: {
            ...(layer.layerStyle?.layout || {}),
          },
        });
        map.addLayer(pointLayer);
      }

      if (layer.layer.includes("FillLayer")) {
        const fillLayer = createFillLayer(layer.id, sourceId, {
          ...layer.layerStyle,
          layout: {
            ...(layer.layerStyle?.layout || {}),
            visibility,
          },
        });
        map.addLayer(fillLayer);
      }

      if (layer.layer.includes("IconLayer")) {
        const iconLayer = createIconLayer(layer.id, sourceId, {
          ...layer.layerStyle,
          layout: {
            ...(layer.layerStyle?.layout || {}),
            visibility,
          },
        });
        map.addLayer(iconLayer);
      }

      // Hover interactions
      if (layer.canHover) {
        map.on("mousemove", layer.id, (e) => {
          if (!e.features?.length) return;

          const feature = e.features[0];
          const featureId = feature.id;

          if (hoveredFeatureId.current !== null) {
            map.setFeatureState(
              { source: sourceId, id: hoveredFeatureId.current },
              { hover: false }
            );
          }

          hoveredFeatureId.current = featureId;
          map.setFeatureState(
            { source: sourceId, id: featureId },
            { hover: true }
          );
          map.getCanvas().style.cursor = "pointer";

          const msg = feature.properties?.displayMessage;
          if (msg && msg.trim()) {
            popupRef.current
              .setLngLat(e.lngLat)
              .setHTML(`<div style="font-size: 14px;">${msg}</div>`)
              .addTo(map);
          }
        });

        map.on("mouseleave", layer.id, () => {
          if (hoveredFeatureId.current !== null) {
            map.setFeatureState(
              { source: sourceId, id: hoveredFeatureId.current },
              { hover: false }
            );
          }
          hoveredFeatureId.current = null;
          map.getCanvas().style.cursor = "";
          popupRef.current.remove();
        });
      }

      // Click interactions
      if (layer.canClick) {
        map.on("click", layer.id, (e) => {
          if (!e.features?.length) return;

          const feature = e.features[0];
          const featureId = feature.id;

          if (clickedFeatureId.current[layer.id] !== undefined) {
            map.setFeatureState(
              {
                source: sourceId,
                id: clickedFeatureId.current[layer.id],
              },
              { click: false }
            );
          }

          clickedFeatureId.current[layer.id] = featureId;
          map.setFeatureState(
            { source: sourceId, id: featureId },
            { click: true }
          );

          const msg = feature.properties?.displayMessage;
          if (msg && msg.trim()) {
            const popupHtml =
              layer.id === "icon-accident-layer"
                ? buildAccidentPopupHtml(msg, feature.properties?.id)
                : layer.parentId === "icon-camera-layer"
                  ? buildInterventionPopupHtml(msg, feature.properties?.id)
                  : `<strong>${msg}</strong>`;

            new mapboxgl.Popup()
              .setLngLat(e.lngLat)
              .setHTML(popupHtml)
              .addTo(map);
          }
        });
      }
    });
  };

  const handleToggle3D = () => {
    setSelectedMapStyle((prev) =>
      prev === mapUIConfig.map.style.map3D
        ? mapUIConfig.map.style.map2D
        : mapUIConfig.map.style.map3D
    );
  };

  const handleLayerToggle = (layerId) => {
    setLayerVisibility((prev) => {
      const targetLayer = layers.find((layer) => layer.id === layerId);
      if (!targetLayer) {
        return prev;
      }

      const newState = { ...prev };

      if (targetLayer.isGroup) {
        const childIds = targetLayer.children || [];
        const nextVisibility = !childIds.every((childId) => !!prev[childId]);

        childIds.forEach((childId) => {
          newState[childId] = nextVisibility;
        });
        newState[layerId] = nextVisibility;
      } else {
        newState[layerId] = !prev[layerId];

        if (targetLayer.parentId) {
          const siblingIds =
            layers.find((layer) => layer.id === targetLayer.parentId)?.children || [];
          newState[targetLayer.parentId] = siblingIds.length
            ? siblingIds.every((childId) => !!newState[childId])
            : newState[targetLayer.parentId];
        }
      }

      const map = mapRef.current;
      if (map) {
        const updatedLayerIds = targetLayer.isGroup
          ? targetLayer.children || []
          : [layerId];

        updatedLayerIds.forEach((updatedLayerId) => {
          if (map.getLayer(updatedLayerId)) {
            map.setLayoutProperty(
              updatedLayerId,
              "visibility",
              newState[updatedLayerId] ? "visible" : "none"
            );
          }
        });
      }

      return newState;
    });
  };

  const renderLayerToggle = ({ layer, isChild }) => {
    const childIds = layer.children || [];
    const selectedChildrenCount = childIds.filter((childId) => !!layerVisibility[childId]).length;
    const checked = layer.isGroup
      ? childIds.length > 0 && selectedChildrenCount === childIds.length
      : !!layerVisibility[layer.id];
    const indeterminate =
      layer.isGroup &&
      selectedChildrenCount > 0 &&
      selectedChildrenCount < childIds.length;

    return (
      <FormControlLabel
        key={layer.id}
        control={
          <Checkbox
            checked={checked}
            indeterminate={indeterminate}
            onChange={() => handleLayerToggle(layer.id)}
            sx={{
              color: panelText,
              ml: isChild ? 2 : 0,
            }}
          />
        }
        label={layer.name}
        sx={{
          color: panelText,
          ml: isChild ? 2 : 0,
        }}
      />
    );
  };

  return (
    <div className="map-container" style={{ position: "relative" }}>
      {/* Desktop control panel */}
      {mapUIConfig.displayControlPanel && !isTablet && (
        <div
          className="control-panel"
          style={{
            background: panelBackground,
            color: panelText,
            border: panelBorder,
            boxShadow: panelShadow,
          }}
        >
          {mapUIConfig.displayLayerList && (
            <div className="layer-list-toggle-controls">
              {layerRows.map(renderLayerToggle)}
            </div>
          )}

          {mapUIConfig.display3DToggle && (
            <FormControlLabel
              control={
                <Switch
                  checked={selectedMapStyle === mapUIConfig.map.style.map3D}
                  onChange={handleToggle3D}
                />
              }
              label={
                selectedMapStyle === mapUIConfig.map.style.map3D ? "3D" : "2D"
              }
              sx={{ color: panelText }}
            />
          )}
        </div>
      )}

      {/* Tablet Layers button (sits under the filter from MapPage) */}
      {isTablet && (
        <>
          <IconButton
            onClick={() => setOpenLayersModal(true)}
                sx={{
                  position: "absolute",
                  top: 72, 
                  right: 16,
                  zIndex: 2,
                  backgroundColor: panelBackground,
                  "&:hover": {
                    backgroundColor: darkMode ? "#283352" : "#f0f2f5",
                  },
                  color: panelText,
                  border: panelBorder,
                  borderRadius: "8px",
                }}
              >
            <Layers />
          </IconButton>

          <Dialog
            open={openLayersModal}
            onClose={() => setOpenLayersModal(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                backgroundColor: panelBackground,
                color: panelText,
                border: panelBorder,
              },
            }}
          >
            <DialogTitle
              sx={{
                backgroundColor: panelBackground,
                color: panelText,
                borderBottom: darkMode
                  ? "1px solid #ffffff33"
                  : "1px solid rgba(0,0,0,0.12)",
                pr: 6,
              }}
            >
              Επίπεδα Χάρτη
              <IconButton
                onClick={() => setOpenLayersModal(false)}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: panelText,
                  borderRadius: "8px",
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent
              sx={{
                backgroundColor: panelBackground,
                color: panelText,
              }}
            >
              <Stack spacing={1}>
                {layerRows.map(renderLayerToggle)}
              </Stack>
            </DialogContent>
          </Dialog>
        </>
      )}

      <div
        id={mapUIConfig.id}
        ref={mapContainerRef}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
}

export default MapboxMapComponent;
